<template>
  <div class="floor-plan-wrapper" ref="wrapperRef">
    <!-- Elegant Loading Overlay -->
    <transition name="fade">
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-content">
          <div class="loading-icon">
            <v-icon size="48" color="primary">mdi-floor-plan</v-icon>
            <div class="spinner"></div>
          </div>
          <p class="loading-text">Preparing Floor Plan...</p>
        </div>
      </div>
    </transition>

    <!-- SVG Container -->
    <div class="svg-viewport" ref="viewportRef">
      <object 
        ref="svgObjectRef"
        type="image/svg+xml" 
        :data="svgPath" 
        @load="handleSvgLoad" 
        class="svg-content"
        :class="{ ready: !isLoading }"
      />
    </div>

    <!-- Floating Controls -->
    <transition name="slide-up">
      <div v-if="!isLoading" class="floating-controls">
        <!-- Hotspot Toggle -->
        <div class="control-item">
          <v-switch 
            v-model="showHotspots" 
            inset 
            color="primary" 
            hide-details 
            density="compact"
          />
          <span class="control-label">Hotspots</span>
        </div>

        <div class="divider"></div>

        <!-- Zoom Controls -->
        <div class="control-item">
          <button @click="handleZoomIn" class="control-btn" :class="{ active: zoomInActive }">
            <v-icon size="18">mdi-plus</v-icon>
          </button>
          <button @click="handleZoomOut" class="control-btn" :class="{ active: zoomOutActive }">
            <v-icon size="18">mdi-minus</v-icon>
          </button>
          <button @click="handleReset" class="control-btn reset-btn" :class="{ active: resetActive }">
            <v-icon size="18">mdi-restore</v-icon>
          </button>
        </div>
      </div>
    </transition>

    <!-- Info Badge -->
    <transition name="info-slide">
      <div v-if="selectedRoom" class="info-badge">
        <span class="room-name">{{ selectedRoom }}</span>
        <button @click="selectedRoom = ''" class="info-close">
          <v-icon size="14">mdi-close</v-icon>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import svgPanZoom from 'svg-pan-zoom';

// Refs
const wrapperRef = ref(null);
const viewportRef = ref(null);
const svgObjectRef = ref(null);

// State
const isLoading = ref(true);
const showHotspots = ref(true);
const selectedRoom = ref('');
const zoomInActive = ref(false);
const zoomOutActive = ref(false);
const resetActive = ref(false);

// SVG Setup
const svgPath = ref('/assets/hbcFloorPlan3dMarked.svg');

// Pan-Zoom Instance
let panZoom = null;
let zoomableRect = null;
let eventListeners = [];
let touchData = { startTime: 0, count: 0 };
let activeTimeouts = [];
let resizeObs = null;
let initialized = false;

// Room Configuration
const ROOM_LAYOUTS = {
  'Big Table': [300, 70, 260, 32, -20, 150],
  '2.02 Auditorium': [320, 70, 200, 30, 0, 0],
  'Toiletten Level 2 Townhall': [350, 90, -210, 28, -90, -50],
  '2.03 Factory Hammerbrooklyn Team': [680, 150, -150, 40, 0, 0],
  '2.06 FH Fixed Desks': [480, 150, -150, 40, 0, -200],
  'Toiletten Level 2 Auditorium': [390, 90, -240, 28, -90, -70],
  'Pantry Level 2': [260, 90, -180, 28, -90, -50],
  '2.05 acitoflux': [260, 90, -180, 28, -90, -50],
  '2.42 FH Team': [250, 90, -180, 28, -90, -50],
  '2.09 shades&contrast GmbH': [390, 90, -250, 28, -90, -50],
  '2.10 Meeting Room': [350, 90, -210, 28, -90, -70],
  '2.04 TikTok Creative Lab': [350, 90, -210, 28, -90, -70],
  '3D FloorPlan': [260, 60, -90, 26, 0, 0],
  '3D Floor Model': [280, 60, -90, 26, 0, 0],
  '3D Building': [300, 60, -90, 26, 0, 0],
};

// Utility: Wait for element dimensions
const waitForDimensions = async (element, timeout = 5000) => {
  const startTime = Date.now();
  return new Promise((resolve, reject) => {
    const check = () => {
      if (!element) {
        reject(new Error('Element not found'));
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        resolve({ width: rect.width, height: rect.height });
      } else if (Date.now() - startTime > timeout) {
        reject(new Error('Timeout waiting for dimensions'));
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });
};

// Utility: Add timeout to tracking
const addTimeout = (fn, delay) => {
  const id = setTimeout(fn, delay);
  activeTimeouts.push(id);
  return id;
};

// Utility: Clear all timeouts
const clearAllTimeouts = () => {
  activeTimeouts.forEach(id => clearTimeout(id));
  activeTimeouts = [];
};

// Utility: Convert screen to SVG coordinates
const screenToSvg = (svg, x, y) => {
  try {
    const point = svg.createSVGPoint();
    point.x = x;
    point.y = y;
    return point.matrixTransform(svg.getScreenCTM().inverse());
  } catch {
    return { x: 0, y: 0 };
  }
};

// Utility: Check if point in zoomable area
const isInZoomArea = (x, y) => {
  if (!zoomableRect) return false;
  try {
    const bbox = zoomableRect.getBBox();
    return x >= bbox.x && x <= bbox.x + bbox.width && 
           y >= bbox.y && y <= bbox.y + bbox.height;
  } catch {
    return false;
  }
};

// Update hotspot visibility
const updateHotspots = (svgDoc) => {
  const paths = svgDoc.querySelectorAll('#Hotspots path');
  paths.forEach(path => {
    const status = path.dataset.status || 'free';
    if (showHotspots.value) {
      path.style.display = 'inline';
      path.style.fill = status === 'booked' ? '#FFCCCC' : '#CCFFCC';
      path.style.stroke = status === 'booked' ? '#FF9999' : '#99FF99';
      path.style.strokeWidth = '2';
      path.style.fillOpacity = '0.5';
    } else {
      path.style.display = 'none';
    }
  });
};

// Highlight path
const highlightPath = (path) => {
  path.style.display = 'inline';
  path.style.stroke = '#3b82f6';
  path.style.strokeWidth = '8';
  path.style.strokeOpacity = '1';
  
  addTimeout(() => {
    const status = path.dataset.status || 'free';
    if (showHotspots.value) {
      path.style.fill = status === 'booked' ? '#FFCCCC' : '#CCFFCC';
      path.style.stroke = status === 'booked' ? '#FF9999' : '#99FF99';
      path.style.strokeWidth = '2';
      path.style.fillOpacity = '0.5';
    } else {
      path.style.display = 'none';
    }
  }, 5000);
};

// Create room buttons
const createRoomButtons = (svgDoc, hotspotsGroup) => {
  hotspotsGroup.querySelectorAll('path').forEach((path, index) => {
    const title = path.querySelector('title')?.textContent?.trim() || `Room ${index + 1}`;
    const isUtility = title.includes('Toilet') || title.includes('Pantry');
    const status = isUtility ? 'free' : (Math.random() > 0.5 ? 'booked' : 'free');
    path.dataset.status = status;

    const bbox = path.getBBox();
    const [width, height, offsetY, fontSize, rotation, offsetX] = 
      ROOM_LAYOUTS[title] || [280, 60, -80, 28, 0, 0];

    const btnX = bbox.x + bbox.width / 2 - width / 2 + offsetX;
    const btnY = bbox.y + offsetY;

    // Create group
    const group = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
    if (rotation !== 0) {
      const cx = btnX + width / 2;
      const cy = btnY + height / 2;
      group.setAttribute('transform', `rotate(${rotation} ${cx} ${cy})`);
    }

    // Create button SVG
    const btnSvg = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    btnSvg.setAttribute('x', btnX);
    btnSvg.setAttribute('y', btnY);
    btnSvg.setAttribute('width', width);
    btnSvg.setAttribute('height', height);
    btnSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    btnSvg.style.cursor = 'pointer';
    btnSvg.style.pointerEvents = 'all';

    // Create button background
    const rect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', width);
    rect.setAttribute('height', height);
    rect.setAttribute('rx', '12');
    rect.setAttribute('fill', 'rgba(59, 130, 246, 0.9)');
    rect.setAttribute('stroke', '#3b82f6');
    rect.setAttribute('stroke-width', '2');
    rect.style.transition = 'all 0.3s ease';

    // Create button text
    const text = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', width / 2);
    text.setAttribute('y', height / 2 + fontSize / 3);
    text.setAttribute('font-size', fontSize);
    text.setAttribute('font-weight', 'bold');
    text.setAttribute('fill', '#fff');
    text.setAttribute('text-anchor', 'middle');
    text.textContent = title;
    text.style.pointerEvents = 'none';

    btnSvg.appendChild(rect);
    btnSvg.appendChild(text);
    group.appendChild(btnSvg);

    // Handle click
    const handleClick = () => {
      selectedRoom.value = title;
      rect.setAttribute('fill', 'rgba(37, 99, 235, 1)');
      rect.setAttribute('stroke', '#1e40af');
      rect.setAttribute('stroke-width', '4');
      rect.setAttribute('filter', 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.8))');
      highlightPath(path);
      
      addTimeout(() => {
        rect.setAttribute('fill', 'rgba(59, 130, 246, 0.9)');
        rect.setAttribute('stroke', '#3b82f6');
        rect.setAttribute('stroke-width', '2');
        rect.removeAttribute('filter');
      }, 5000);
    };

    const clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleClick();
    };

    btnSvg.addEventListener('click', clickHandler);
    btnSvg.addEventListener('touchstart', clickHandler, { passive: false });
    
    eventListeners.push(
      { element: btnSvg, type: 'click', listener: clickHandler },
      { element: btnSvg, type: 'touchstart', listener: clickHandler }
    );

    hotspotsGroup.appendChild(group);
    path.style.pointerEvents = 'none';
  });
};

// Initialize SVG
const initializeSvg = async (svgDoc, svgElement) => {
  // Set viewBox
  if (!svgElement.getAttribute('viewBox')) {
    svgElement.setAttribute('viewBox', '0 0 5333.3335 3333.3333');
  }

  // Create zoomable area
  zoomableRect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
  zoomableRect.setAttribute('x', '1000');
  zoomableRect.setAttribute('y', '500');
  zoomableRect.setAttribute('width', '3333');
  zoomableRect.setAttribute('height', '2333');
  zoomableRect.setAttribute('fill', 'none');
  zoomableRect.setAttribute('pointer-events', 'all');
  svgElement.appendChild(zoomableRect);

  // Initialize pan-zoom
  panZoom = svgPanZoom(svgElement, {
    zoomEnabled: true,
    panEnabled: false,
    controlIconsEnabled: false,
    mouseWheelZoomEnabled: true,
    dblClickZoomEnabled: false,
    preventMouseEventsDefault: false,
    zoomScaleSensitivity: 0.2,
    fit: true,
    center: true,
    beforeZoom: (oldZoom, newZoom) => {
      if (newZoom > 1 && !panZoom.isPanEnabled()) {
        panZoom.enablePan();
      } else if (newZoom <= 1 && panZoom.isPanEnabled()) {
        panZoom.disablePan();
      }
      return true;
    },
  });

  // Touch handlers
  const touchStart = (e) => {
    touchData.startTime = Date.now();
    touchData.count = e.touches.length;

    if (e.touches.length === 1) {
      e.preventDefault();
    } else if (e.touches.length >= 2) {
      const t1 = screenToSvg(svgElement, e.touches[0].clientX, e.touches[0].clientY);
      const t2 = screenToSvg(svgElement, e.touches[1].clientX, e.touches[1].clientY);
      if (!isInZoomArea(t1.x, t1.y) || !isInZoomArea(t2.x, t2.y)) {
        e.preventDefault();
      }
    }
  };

  const touchEnd = (e) => {
    if (touchData.count === 1 && Date.now() - touchData.startTime < 300) {
      e.preventDefault();
    }
  };

  svgElement.addEventListener('touchstart', touchStart, { passive: false });
  svgElement.addEventListener('touchend', touchEnd, { passive: false });
  eventListeners.push(
    { element: svgElement, type: 'touchstart', listener: touchStart },
    { element: svgElement, type: 'touchend', listener: touchEnd }
  );

  // Create room buttons
  const hotspotsGroup = svgDoc.querySelector('#Hotspots');
  if (hotspotsGroup) {
    createRoomButtons(svgDoc, hotspotsGroup);
    updateHotspots(svgDoc);
  }

  // Center and fit
  await nextTick();
  addTimeout(() => {
    if (panZoom) {
      panZoom.resize();
      panZoom.fit();
      panZoom.center();
      panZoom.disablePan();
      panZoom.updateBBox();
    }
  }, 100);
};

// Handle SVG Load
const handleSvgLoad = async () => {
  if (initialized) return;

  try {
    await waitForDimensions(viewportRef.value);
    await new Promise(resolve => setTimeout(resolve, 200));

    const svgObj = svgObjectRef.value;
    const svgDoc = svgObj?.contentDocument;
    const svgEl = svgDoc?.querySelector('svg');

    if (!svgEl) throw new Error('SVG element not found');

    await initializeSvg(svgDoc, svgEl);
    initialized = true;
    
    addTimeout(() => {
      isLoading.value = false;
    }, 300);

  } catch (error) {
    console.error('Failed to load floor plan:', error);
    isLoading.value = false;
  }
};

// Control Handlers
const handleZoomIn = () => {
  if (panZoom) {
    panZoom.zoomIn();
    zoomInActive.value = true;
    addTimeout(() => { zoomInActive.value = false; }, 300);
  }
};

const handleZoomOut = () => {
  if (panZoom) {
    panZoom.zoomOut();
    zoomOutActive.value = true;
    addTimeout(() => { zoomOutActive.value = false; }, 300);
  }
};

const handleReset = () => {
  if (panZoom) {
    panZoom.resetZoom();
    panZoom.fit();
    panZoom.center();
    panZoom.disablePan();
    resetActive.value = true;
    addTimeout(() => { resetActive.value = false; }, 300);
  }
};

// Resize Handler
const handleResize = () => {
  if (panZoom && initialized && !isLoading.value) {
    try {
      panZoom.resize();
      panZoom.fit();
      panZoom.center();
      panZoom.disablePan();
    } catch (error) {
      console.warn('Resize error:', error);
    }
  }
};

// Cleanup
const cleanup = () => {
  initialized = false;
  clearAllTimeouts();

  if (resizeObs) {
    resizeObs.disconnect();
    resizeObs = null;
  }

  if (panZoom) {
    try {
      panZoom.destroy();
    } catch (error) {
      console.warn('Error destroying panZoom:', error);
    }
    panZoom = null;
  }

  eventListeners.forEach(({ element, type, listener }) => {
    try {
      element.removeEventListener(type, listener);
    } catch (error) {
      console.warn('Error removing listener:', error);
    }
  });
  eventListeners = [];

  if (zoomableRect) {
    try {
      const svgObj = svgObjectRef.value;
      const svgDoc = svgObj?.contentDocument;
      const svgEl = svgDoc?.querySelector('svg');
      if (svgEl && svgEl.contains(zoomableRect)) {
        svgEl.removeChild(zoomableRect);
      }
    } catch (error) {
      console.warn('Error removing zoomable rect:', error);
    }
    zoomableRect = null;
  }

  window.removeEventListener('resize', handleResize);
};

// Watch hotspots toggle
watch(showHotspots, () => {
  if (!initialized) return;
  const svgObj = svgObjectRef.value;
  const svgDoc = svgObj?.contentDocument;
  if (svgDoc) updateHotspots(svgDoc);
});

// Lifecycle
onMounted(() => {
  window.addEventListener('resize', handleResize);
  
  if (wrapperRef.value) {
    resizeObs = new ResizeObserver(handleResize);
    resizeObs.observe(wrapperRef.value);
  }
});

onBeforeUnmount(() => {
  cleanup();
});

defineExpose({ handleReset });
</script>

<style scoped>
.floor-plan-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background: transparent;
  overflow: hidden;
}

/* ==================== Loading Screen ==================== */
.loading-screen {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(248, 250, 252, 0.96) 100%
  );
  backdrop-filter: blur(20px);
  z-index: 100;
  border-radius: 12px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.loading-icon {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  position: absolute;
  inset: -10px;
  border: 3px solid transparent;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

/* ==================== SVG Viewport ==================== */
.svg-viewport {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(59, 130, 246, 0.1);
  min-height: 0;
}

.svg-content {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.svg-content.ready {
  opacity: 1;
}

/* ==================== Floating Controls ==================== */
.floating-controls {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 10px 16px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(248, 250, 252, 0.95) 100%
  );
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  z-index: 50;
}

.control-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  white-space: nowrap;
}

.divider {
  width: 1px;
  height: 24px;
  background: rgba(59, 130, 246, 0.2);
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.control-btn:hover {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
}

.control-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
  transform: scale(0.95);
}

/* ==================== Info Badge ==================== */
.info-badge {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  z-index: 60;
  max-width: calc(100% - 2rem);
}

.room-name {
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.info-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* ==================== Transitions ==================== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.info-slide-enter-active,
.info-slide-leave-active {
  transition: all 0.3s ease;
}

.info-slide-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

.info-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

/* ==================== Responsive - Tablet ==================== */
@media (max-width: 1024px) {
  .floating-controls {
    bottom: 1.25rem;
    padding: 8px 14px;
    gap: 0.875rem;
  }

  .control-btn {
    width: 34px;
    height: 34px;
  }

  .control-label {
    font-size: 0.8125rem;
  }
}

/* ==================== Responsive - Mobile ==================== */
@media (max-width: 768px) {
  .floating-controls {
    bottom: 0.75rem;
    padding: 6px 10px;
    gap: 0.5rem;
    max-width: calc(100% - 1.5rem);
  }

  .control-item {
    gap: 0.375rem;
  }

  .control-label {
    font-size: 0.75rem;
  }

  .divider {
    height: 20px;
  }

  .control-btn {
    width: 32px;
    height: 32px;
  }

  .info-badge {
    top: 0.75rem;
    padding: 6px 12px;
  }

  .room-name {
    font-size: 0.8125rem;
  }

  .info-close {
    width: 18px;
    height: 18px;
  }
}

/* ==================== Responsive - Small Mobile ==================== */
@media (max-width: 480px) {
  .floating-controls {
    bottom: 0.5rem;
    padding: 5px 8px;
    gap: 0.375rem;
    max-width: calc(100% - 1rem);
  }

  .control-item {
    gap: 0.25rem;
  }

  .control-label {
    font-size: 0.6875rem;
  }

  .divider {
    height: 18px;
  }

  .control-btn {
    width: 30px;
    height: 30px;
  }

  .info-badge {
    top: 0.5rem;
    padding: 5px 10px;
    max-width: calc(100% - 1rem);
  }

  .room-name {
    font-size: 0.75rem;
  }

  .info-close {
    width: 16px;
    height: 16px;
  }

  .loading-icon {
    width: 60px;
    height: 60px;
  }

  .loading-text {
    font-size: 1rem;
  }
}

/* ==================== Touch Devices ==================== */
@media (hover: none) and (pointer: coarse) {
  .control-btn {
    min-width: 40px;
    min-height: 40px;
  }
}
</style>